/* -------------------------------------------------------------------------- */
/* Dependencies                                */
/* -------------------------------------------------------------------------- */
import { Button, Title } from '@components/v2/ui';
import { Box, Flex } from '@mantine/core';
import useStore from '@store/index';
import { uuid } from '@utils/functions';
import { useTranslation } from 'react-i18next';
import { Stepper } from '../Stepper/Stepper';
import classes from './StepperHeader.module.css';

/* -------------------------------------------------------------------------- */
/* Config                                   */
/* -------------------------------------------------------------------------- */

interface Step {
	key: string;
	label: string;
}

export const STEPS: Step[] = [
	{
		key: uuid(),
		label: 'mint.stepper.selection', // "Select Productions"
	},
	{
		key: uuid(),
		label: 'mint.stepper.confirmation', // "Confirm Mint"
	},
];

/* -------------------------------------------------------------------------- */
/* Types                                     */
/* -------------------------------------------------------------------------- */

interface StepperHeaderProps {
	onCancel: () => void;
	onPrevious: () => void;
	onNext: () => void;
	activeStep: number;
}

/* -------------------------------------------------------------------------- */
/* Component                                   */
/* -------------------------------------------------------------------------- */

const StepperHeader = ({
	onCancel,
	onPrevious,
	onNext,
	activeStep,
}: StepperHeaderProps) => {
	const { t } = useTranslation();

	/* -------------------------- Store data -------------------------- */
	const { selectedProductionsForMint } = useStore();

	/* -------------------------- Step flags -------------------------- */
	const isStep1 = activeStep === 0;

	/* -------------------------- Validation -------------------------- */

	/* -------------------------- Step flags -------------------------- */
	const isFirstStep = activeStep === 0;
	const isLastStep = activeStep === STEPS.length - 1;

	/* -------------------------- UI Logic -------------------------- */

	// The button displays "Next" on step 1, and "Validate my choice" on step 2
	const nextButtonLabel = isLastStep
		? t('mint.actions.validate')
		: t('global.next');

	const isNextDisabled =
		(isFirstStep &&
			selectedProductionsForMint.length === 0) ||
		selectedProductionsForMint.length === 0;

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes['container-header']}>
			<Flex
				wrap="wrap"
				justify="space-between"
				align="center"
				gap="xs"
			>
				<Flex gap="12px" align="center">
					<Button
						w={160}
						variant="outline"
						type="button"
						onClick={onCancel}
					>
						{t('global.cancel')}
					</Button>
					<Title
						ta="center"
						c="var(--mantine-color-igluPrimary-9)"
						order={6}
					>
						{t('mint.title')}
					</Title>
				</Flex>

				<Flex gap="12px" align="center">
					<Button
						w={160}
						variant="outline"
						type="button"
						onClick={onPrevious}
						disabled={isStep1}
					>
						{t('global.previous')}
					</Button>
					<Button
						miw={160}
						variant="primary"
						type="button"
						onClick={onNext}
						disabled={isNextDisabled}
					>
						{nextButtonLabel}
					</Button>
				</Flex>
			</Flex>

			<Box mx={'20%'} mt="md">
				<Stepper
					readOnly
					options={STEPS}
					active={activeStep}
					iconSize={32}
				/>
			</Box>
		</Box>
	);
};

export default StepperHeader;
